import React, { useState, useEffect } from "react";
import Typography from "components/typography";
import Icon from "components/icon";
import Select from "components/select/select";
import Input from "components/input";
import GoogleMapsProvider from "components/map/GoogleMapsProvider";
import RouteMap from "components/map/RouteMap";
import DriverSlipView from "./components/DriverSlipView";
import StepIndicator from "./components/StepIndicator";
import SegmentSection from "./components/SegmentSection";
import { useDriverTripCalculatorForm } from "./useDriverTripCalculatorForm";
import {
  useTripCalculatorServiceV1GetTripCalculatorVehicleTypesQuery,
  useTripCalculatorServiceV1GetTripCalculatorTripTypesQuery,
  useTripCalculatorServiceV1GetTripCalculatorVehicleModelsQuery,
} from "_services/modules/trip-calculator";
import { useTripTypeServiceV1ListTripTypesQuery } from "_services/modules/trip_type";
import ConfirmationModal from "components/shared/confirmation-modal";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { resetDtcState } from "store/driver-trip-calculator";
import { SEGMENT_COLORS } from "_interfaces/driverTripCalculator.interfaces";
import { Controller } from "react-hook-form";
import { useAppTranslation } from "locale/useAppTranslation";

export const driverTripCalculatorPageRouteName = "/dtc/driver-trip-calculator";

const DriverTripCalculatorPage = () => {
  const dispatch = useAppDispatch();
  const { t } = useAppTranslation("driverTripCalculator");

  useEffect(() => {
    dispatch(setGlobalComponent({ hasBackButton: false, showSideBar: false }));
    return () => {
      dispatch(resetDtcState());
    };
  }, [dispatch]);

  const { data: vehicleTypesData } = useTripCalculatorServiceV1GetTripCalculatorVehicleTypesQuery();
  const { data: tripTypesData } = useTripCalculatorServiceV1GetTripCalculatorTripTypesQuery();
  const { data: tripTypeListData } = useTripTypeServiceV1ListTripTypesQuery({ page: 1, size: 100 });

  // Build a set of trip type IDs where is_freezer is true, for use in showFreezerIdleTime
  const freezerTripTypeIds = new Set(
    (tripTypeListData?.trip_types ?? [])
      .filter((tt) => tt.is_freezer === true)
      .map((tt) => tt.id ?? "")
      .filter(Boolean)
  );

  // The first freezer trip type ID is used for form validation
  const freezerTripTypeId = Array.from(freezerTripTypeIds)[0] ?? "";

  const {
    control,
    errors,
    handleSubmit,
    watch,
    setValue,
    segmentFields,
    appendSegment,
    removeSegment,
    currentStep,
    calculationResult,
    isCalculating,
    segmentRoutes,
    handleVehicleTypeChange,
    handleNextToStep2,
    handleCalculate,
    handleReset,
    handleBackToStep2,
    handleBackToStep1,
    updateSegmentRoute,
    getTotalRouteInfo,
    watchedVehicleTypeId,
  } = useDriverTripCalculatorForm(freezerTripTypeId);

  const { data: vehicleModelsData } = useTripCalculatorServiceV1GetTripCalculatorVehicleModelsQuery(
    { vehicleTypeId: watchedVehicleTypeId },
    { skip: !watchedVehicleTypeId }
  );

  const [showRemoveSegmentConfirmation, setShowRemoveSegmentConfirmation] = useState<number | null>(null);

  const watchedVehicleModelId = watch("vehicle_model_id");
  const watchedSegments = watch("segments");

  // Vehicle options from API
  const vehicleTypeOptions = (vehicleTypesData?.vehicleTypes ?? []).map((vt) => ({
    value: vt.id ?? "",
    label: vt.name ?? "",
  }));
  const vehicleModelOptions = (vehicleModelsData?.vehicleModels ?? []).map((vm) => ({
    value: vm.id ?? "",
    label: vm.name ?? "",
  }));

  // Trip type options from API
  const tripTypeOptions = (tripTypesData?.tripTypes ?? []).map((tt) => ({
    value: tt.id ?? "",
    label: tt.name ?? "",
  }));

  const handleRemoveSegmentConfirm = (index: number) => {
    setShowRemoveSegmentConfirmation(index);
  };

  const handleConfirmRemoveSegment = () => {
    if (showRemoveSegmentConfirmation !== null) {
      removeSegment(showRemoveSegmentConfirmation);
      setShowRemoveSegmentConfirmation(null);
    }
  };

  // Map segments data for RouteMap — not memoized so RouteMap always sees fresh form values
  const mapSegments = (watchedSegments || []).map((seg, idx) => ({
    segmentIndex: idx,
    locations: (seg.locations || []).map((loc) => ({
      name: loc.name,
      lat: loc.lat,
      lng: loc.lng,
      avoid_toll: loc.avoid_toll,
    })),
  }));

  const { totalDistance, totalTime } = getTotalRouteInfo();

  return (
    <GoogleMapsProvider>
      <div className="flex flex-col gap-4">
        {/* Page Header */}
        <section className="rounded-xl border border-border bg-white px-6 py-4 shadow-sm">
          <h1 className="text-xl font-bold text-text-primary">{t("page-title")}</h1>
        </section>

        {/* Main Layout: Left Panel + Right Map */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* LEFT PANEL — Form Steps */}
          <div>
            <div className="rounded-xl border border-border bg-white shadow-sm">
              {/* Collapsible Header */}
              <div className="flex items-center justify-between px-6 py-4">
                <Typography variant="heading2" className="text-base font-semibold">
                  {t("panel-title")}
                </Typography>
                <Icon name="chevron-up" className="h-5 w-5 text-gray-400" />
              </div>

              <div className="px-6 pb-6">
                {/* Step Indicators */}
                <StepIndicator currentStep={currentStep} />

                {/* STEP 1: Vehicle Selection + Trip Info */}
                {currentStep === 1 && (
                  <div className="mt-6 space-y-4">
                    {/* Row 1: Customer + Temperature */}
                    <div className="grid grid-cols-2 gap-4">
                      <Controller
                        name="customer"
                        control={control}
                        render={({ field, fieldState }) => (
                          <Input
                            {...field}
                            label={t("field-customer")}
                            placeholder={t("field-customer")}
                            required
                            error={fieldState.error}
                            data-testid="input-dtc-customer"
                          />
                        )}
                      />
                      <Controller
                        name="temperature"
                        control={control}
                        render={({ field, fieldState }) => (
                          <Input
                            {...field}
                            label={t("field-temperature")}
                            placeholder="0"
                            type="number"
                            required
                            error={fieldState.error}
                            value={field.value ?? ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            data-testid="input-dtc-temperature"
                          />
                        )}
                      />
                    </div>

                    {/* Row 2: Plate Number + Vehicle Type + Vehicle Model */}
                    <div className="grid grid-cols-3 gap-4">
                      <Controller
                        name="plate_number"
                        control={control}
                        render={({ field, fieldState }) => (
                          <Input
                            {...field}
                            label={t("field-plate-number")}
                            placeholder={t("field-plate-number")}
                            required
                            error={fieldState.error}
                            data-testid="input-dtc-plate-number"
                          />
                        )}
                      />
                      <Controller
                        name="vehicle_type_id"
                        control={control}
                        render={({ field, fieldState }) => (
                          <Select
                            {...field}
                            label={t("field-vehicle-type")}
                            options={vehicleTypeOptions}
                            value={field.value}
                            onValueChange={(value) => {
                              if (typeof value === "string") handleVehicleTypeChange(value);
                            }}
                            placeholder={t("field-vehicle-type")}
                            required
                            error={fieldState.error}
                            data-testid="dropdown-dtc-vehicle-type"
                          />
                        )}
                      />
                      <Controller
                        name="vehicle_model_id"
                        control={control}
                        render={({ field, fieldState }) => (
                          <Select
                            {...field}
                            label={t("field-vehicle-model")}
                            options={vehicleModelOptions}
                            value={field.value}
                            onValueChange={field.onChange}
                            placeholder={t("field-vehicle-model")}
                            required
                            error={fieldState.error}
                            data-testid="dropdown-dtc-vehicle-model"
                          />
                        )}
                      />
                    </div>

                    {/* Next Button */}
                    <button
                      type="button"
                      onClick={handleNextToStep2}
                      className="w-full rounded-lg bg-primary-500 px-4 py-3 font-medium text-white hover:bg-primary-600 transition-colors"
                      data-testid="btn-dtc-next"
                    >
                      {t("btn-next")}
                    </button>
                  </div>
                )}

                {/* STEP 2: Trip Segments — all in left panel */}
                {currentStep === 2 && (
                  <form onSubmit={handleSubmit(handleCalculate)} className="mt-6 space-y-5">
                    {segmentFields.map((field, index) => {
                      const segmentColor = SEGMENT_COLORS[index % SEGMENT_COLORS.length];
                      const route = segmentRoutes[index];
                      const watchedTripTypeId = watch(`segments.${index}.trip_type_id`);
                      const showFreezerIdleTime = freezerTripTypeIds.has(watchedTripTypeId);
                      const watchedEnableAdditionalCost = watch(`segments.${index}.enable_additional_cost`);

                      return (
                        <SegmentSection
                          key={field.id}
                          index={index}
                          segmentColor={segmentColor}
                          route={route}
                          control={control}
                          watch={watch}
                          setValue={setValue}
                          errors={errors}
                          tripTypeOptions={tripTypeOptions}
                          showFreezerIdleTime={showFreezerIdleTime}
                          watchedEnableAdditionalCost={watchedEnableAdditionalCost}
                          canRemove={segmentFields.length > 1}
                          onRemove={() => handleRemoveSegmentConfirm(index)}
                          onRouteUpdate={(d, t, pairs) => updateSegmentRoute(index, d, t, pairs)}
                        />
                      );
                    })}

                    {/* Add More Segment */}
                    <button
                      type="button"
                      onClick={appendSegment}
                      disabled={segmentFields.length >= 10}
                      className="w-full rounded-lg border-2 border-dashed border-primary-300 bg-white px-4 py-3 font-medium text-primary-600 hover:bg-primary-50 transition-colors disabled:opacity-50"
                      data-testid="btn-dtc-add-segment"
                    >
                      {t("btn-add-segment")}
                    </button>

                    {/* Bottom Actions */}
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={handleBackToStep1}
                        className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        {t("btn-back")}
                      </button>
                      <button
                        type="submit"
                        disabled={isCalculating}
                        className="flex-1 rounded-lg bg-primary-500 px-4 py-3 font-semibold text-white hover:bg-primary-600 transition-colors disabled:opacity-50"
                        data-testid="btn-dtc-calculate"
                      >
                        {isCalculating ? t("btn-calculating") : t("btn-calculate")}
                      </button>
                    </div>
                  </form>
                )}

                {/* STEP 3: Driver Slip — inline */}
                {currentStep === 3 && calculationResult && (
                  <div className="mt-6">
                    <DriverSlipView
                      calculationResult={calculationResult}
                      onBack={handleBackToStep2}
                      onReset={handleReset}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT PANEL — Map */}
          <div className="lg:sticky lg:top-4 lg:self-start">
            <RouteMap segments={mapSegments} className="min-h-[calc(100vh-120px)]" />
          </div>
        </div>

        {/* Remove Segment Confirmation */}
        <ConfirmationModal
          open={showRemoveSegmentConfirmation !== null}
          onCancel={() => setShowRemoveSegmentConfirmation(null)}
          onConfirm={handleConfirmRemoveSegment}
          title={t("modal-remove-segment-title")}
          message={t("modal-remove-segment-message", {
            number: showRemoveSegmentConfirmation !== null ? showRemoveSegmentConfirmation + 1 : "",
          })}
          confirmLabel={t("modal-remove-segment-confirm")}
          cancelLabel={t("btn-cancel")}
        />
      </div>
    </GoogleMapsProvider>
  );
};

export default DriverTripCalculatorPage;

